import * as React from 'react';
// @ts-expect-error
import {DragSource, ConnectDragSource, ConnectDragPreview} from 'react-dnd';
// @ts-expect-error
import {getEmptyImage} from 'react-dnd-html5-backend';
import {connect} from 'react-redux';

import * as Leads from 'nutshell-core/leads';
import type {ListItem, Lead, LeadId} from 'nutshell-core/types';

import {getActionOptionsForEntities} from 'shells/list-item/get-action-options-for-entities';
import {LEAD_BOARD_TOUR_CARD_DATA_TARGET} from 'shells/tours/tours';

import type {NutshellState} from '../../../store';

import {dragItemTypes} from '../../dnd/drag-item-types';
import type {DndVisualPipelineLeadCardItem} from '../../dnd/drag-item-types';
import {openComposerWithId} from '../../email/email-composer-actions';
import {LeadCardDynamicWrapper} from '../../lead/lead-card-dynamic';
// @ts-expect-error
import {fetchLeadsSchema} from '../../master-dashboard/dashboard-actions';

import styles from './visual-pipeline-lead-card.module.css';

type OwnProps = {
    listItem: ListItem;

    stageId: string;
    canDrag: boolean;
    // Is this the card that is either in the middle of a stage change
    // or actively being close (i.e. confirming if it should be won)

    isChangingState: boolean;

    onBeginDrag: (leadId: string) => any;

    onCardDrop: (listItem: ListItem, leadId: string, dropResult?: any) => any;
    // From react-dnd
    connectDragSource: ConnectDragSource;
    connectDragPreview: ConnectDragPreview;
    isDragging?: boolean;
    fieldsToDisplay: string[];
    isLoadingFields: boolean;
};

type StateProps = {
    leadModel?: Lead;
};

type DispatchProps = {
    onOpenLeadActionMenu: (leadId: LeadId) => any;
    onEmailEntity: (entityId: string) => any;
    fetchLeadsSchema: () => any;
};

const mapStateToProps = function (state: NutshellState, ownProps: OwnProps): StateProps {
    const leadId = ownProps.listItem.links.entity.id;

    return {
        leadModel: Leads.getLead(state, leadId),
    };
};

// @ts-expect-error
const mapDispatchToProps = function (dispatch): DispatchProps {
    return {
        onOpenLeadActionMenu: (leadId: LeadId) => {
            dispatch(Leads.fetchLeadById(leadId));
        },
        onEmailEntity: (entityId: string) => {
            dispatch(openComposerWithId(entityId));
        },
        fetchLeadsSchema: () => fetchLeadsSchema(),
    };
};

type Props = OwnProps & StateProps & DispatchProps;

/*
Acts as a wrapper component around a LeadCard, adding a Drag and Drop interface
to the card, as well as rendering an "empty" card when the card is being dragged
*/
export class VisualPipelineLeadCardComponent extends React.PureComponent<Props> {
    componentDidMount() {
        this.props.fetchLeadsSchema();
        // Use empty image as a drag preview so browsers don't draw it
        // and we can draw whatever we want on the custom drag layer instead.
        this.props.connectDragPreview(getEmptyImage(), {
            // IE fallback: specify that we'd rather screenshot the node
            // when it already knows it's being dragged so we can hide it with CSS.
            captureDraggingState: true,
        });
    }

    render() {
        return (
            <div
                data-tour-target={LEAD_BOARD_TOUR_CARD_DATA_TARGET}
                className={
                    this.props.canDrag
                        ? styles['lead-card-preview-wrapper-draggable']
                        : styles['lead-card-preview-wrapper']
                }
            >
                {this.props.connectDragSource(
                    // ConnectDragSource requires a standard dom node as the
                    // parent element, not a component, thus the wrapping <div>
                    // Also, for IE, we need to set opacity here, because the empty image doesn't work
                    <div style={{opacity: this.props.isDragging ? 0 : 1}}>
                        <LeadCardDynamicWrapper
                            linkable={true}
                            isDraggable={this.props.canDrag}
                            onActionMenuOpen={this.handleActionMenuOpen}
                            // @ts-expect-error
                            options={this.getLeadCardActionMenu()}
                            listItem={this.props.listItem}
                            fieldsToDisplay={this.props.fieldsToDisplay}
                            isLoadingFields={this.props.isLoadingFields}
                            anchorElementId={this.getAnchorElementId()}
                        />
                    </div>
                )}
            </div>
        );
    }

    handleActionMenuOpen = (leadId: LeadId) => {
        this.props.onOpenLeadActionMenu(leadId);
    };

    getLeadCardActionMenu = () => {
        if (!this.props.leadModel) {
            return [];
        }

        const relatedEntities = this.props.leadModel.accounts.concat(this.props.leadModel.contacts);
        const actionOptions = getActionOptionsForEntities(relatedEntities, {
            email: this.props.onEmailEntity,
            call: this.handleCallEntity,
        });

        return actionOptions.length
            ? actionOptions
            : [{key: 'empty', label: 'No related contact info', isDisabled: true}];
    };

    handleCallEntity = ({
        entityId,
        phoneNumber,
    }: {
        entityId: string;
        phoneNumber: {
            E164: string;
            numberFormatted: string;
        };
    }) => {
        $(window).trigger(Peep.events.showReactClickToCallPopover, {
            entityId,
            anchorElementId: this.getAnchorElementId(),
            rawPhoneNumber: phoneNumber.E164,
            formattedPhoneNumber: phoneNumber.numberFormatted,
        });
    };

    getAnchorElementId = () => {
        return this.props.listItem.links.entity.id;
    };
}

const leadCardSource = {
    // @ts-expect-error
    canDrag(props) {
        return props.canDrag;
    },
    // @ts-expect-error
    beginDrag(props): DndVisualPipelineLeadCardItem {
        props.onBeginDrag(props.listItem.links.entity.id);

        // Return the data describing the dragged item
        return {
            id: props.listItem.links.entity.id,
            stageId: props.stageId,
            listItem: props.listItem,
            fieldsToDisplay: props.fieldsToDisplay,
        };
    },
    // @ts-expect-error
    endDrag(props, monitor) {
        if (!monitor.didDrop()) {
            // Did not drop over a valid target, so we'll just
            // return the expected dragItem without a valid dropResult
            props.onCardDrop(props.listItem, props.stageId);

            // Short-curcuit our return so we don't call a real drop
            return;
        }

        // We don't konw which _type_ of dropTarget this card was dropped onto,
        // but we do know we need the ListItem, and stageId from which it came.
        //
        // We'll include the DropResult (which varies based on the type of
        // DropTarget) so that we can determine the type and associated payload
        // of the given dropTarget.
        props.onCardDrop(props.listItem, props.stageId, monitor.getDropResult());
    },
} as const;

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
const VisualPipelineLeadCardConnected = connector(VisualPipelineLeadCardComponent);

// @ts-expect-error
const leadCardConnect = (connectDnd, monitor) => ({
    connectDragSource: connectDnd.dragSource(),
    connectDragPreview: connectDnd.dragPreview(),
    isDragging: monitor.isDragging(),
});

export const VisualPipelineLeadCard = DragSource(
    dragItemTypes.LEAD_CARD,
    leadCardSource,
    leadCardConnect
)(VisualPipelineLeadCardConnected);
